import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { apiUrl } from '../apiBase';

const emptyStudent = () => ({ first_name: '', last_name: '', grade_level: '' });

export default function ParentIntroductionForm() {
  const { token } = useParams();
  const [formInfo, setFormInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [parentFirstName, setParentFirstName] = useState('');
  const [parentLastName, setParentLastName] = useState('');
  const [students, setStudents] = useState([emptyStudent()]);
  const [note, setNote] = useState('');

  useEffect(() => {
    let active = true;
    fetch(apiUrl(`/parent-introductions/form/${encodeURIComponent(token)}`))
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.detail || 'Form açılamadı.');
        return body;
      })
      .then((body) => { if (active) { if (body.status === 'already_received') setDone(true); else setFormInfo(body); } })
      .catch((cause) => { if (active) setError(cause.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  const updateStudent = (index, field, value) => {
    setStudents((current) => current.map((student, position) => position === index ? { ...student, [field]: value } : student));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setSending(true);
    try {
      const response = await fetch(apiUrl(`/parent-introductions/form/${encodeURIComponent(token)}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parent_first_name: parentFirstName.trim(),
          parent_last_name: parentLastName.trim(),
          students: students.map((student) => Object.fromEntries(Object.entries(student).map(([key, value]) => [key, value.trim()]))),
          parent_note: note.trim() || null,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'Form gönderilemedi. Bilgilerinizi kontrol edip tekrar deneyin.');
      setDone(true);
    } catch (cause) {
      setError(cause.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f8f5', py: { xs: 3, sm: 7 } }}>
      <Container maxWidth="sm">
        <Paper elevation={2} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>Tanışma Formu</Typography>
          {formInfo?.school_name && <Typography variant="h6" color="text.secondary" gutterBottom>{formInfo.school_name}</Typography>}
          {loading && <Typography>Form yükleniyor…</Typography>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {done ? (
            <Stack spacing={2}>
              <Typography variant="h5">Teşekkür ederiz 🌿</Typography>
              <Typography>Bilgileriniz bize ulaştı. En kısa sürede sizinle iletişime geçip merak ettiklerinizi yanıtlayacağız; dilerseniz uygun bir görüşme zamanı da planlayabiliriz.</Typography>
            </Stack>
          ) : formInfo && (
            <Box component="form" onSubmit={submit}>
              <Typography sx={{ mb: 3 }}>Sizi biraz daha yakından tanıyabilmemiz için birkaç kısa bilgi rica ediyoruz. Form yaklaşık 1 dakika sürer. 🌿</Typography>
              <Stack spacing={2}>
                <TextField label="Adınız" value={parentFirstName} onChange={(event) => setParentFirstName(event.target.value)} required inputProps={{ maxLength: 128 }} />
                <TextField label="Soyadınız" value={parentLastName} onChange={(event) => setParentLastName(event.target.value)} required inputProps={{ maxLength: 128 }} />
                {students.map((student, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle1" fontWeight={700}>{index + 1}. Çocuk</Typography>
                        {students.length > 1 && <IconButton aria-label={`${index + 1}. çocuğu kaldır`} onClick={() => setStudents((current) => current.filter((_, position) => position !== index))}><DeleteOutline /></IconButton>}
                      </Stack>
                      <TextField label="Çocuğunuzun adı" value={student.first_name} onChange={(event) => updateStudent(index, 'first_name', event.target.value)} required inputProps={{ maxLength: 128 }} />
                      <TextField label="Çocuğunuzun soyadı" value={student.last_name} onChange={(event) => updateStudent(index, 'last_name', event.target.value)} required inputProps={{ maxLength: 128 }} />
                      <TextField label="Sınıf seviyesi" value={student.grade_level} onChange={(event) => updateStudent(index, 'grade_level', event.target.value)} required inputProps={{ maxLength: 64 }} />
                    </Stack>
                  </Paper>
                ))}
                {students.length < 10 && <Button startIcon={<Add />} onClick={() => setStudents((current) => [...current, emptyStudent()])}>Başka bir çocuk ekle</Button>}
                <TextField label="Bizimle paylaşmak istediğiniz bir konu var mı?" value={note} onChange={(event) => setNote(event.target.value)} multiline minRows={2} inputProps={{ maxLength: 2000 }} />
                <Typography variant="body2" color="text.secondary">
                  Kişisel verilerinizin işlenmesine ilişkin <Link href={formInfo.privacy_notice_url} target="_blank" rel="noopener noreferrer">Aydınlatma Metni</Link>’ni inceleyebilirsiniz.
                </Typography>
                <Button type="submit" variant="contained" size="large" disabled={sending}>{sending ? 'Gönderiliyor…' : 'Bilgilerimi gönder'}</Button>
              </Stack>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
